import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Timelapse as TimelapseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowDropDown as ArrowDropDownIcon
} from "@material-ui/icons";
import styled from "styled-components";
import UserStore from "../../utils/UserStore";
import Button from "../../waybee-ui/Button";
import { groupActionsByAudience } from "./actionGroups";
import {
  ACTION_STATUS,
  getActionStatus,
  getActionStatusLabel
} from "./ataDraftStore";
import {
  formatSpeakerMetaLine,
  listPersonOptions,
  normalizeNameKey
} from "./speakers";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

const GroupTitle = styled(Typography)`
  && {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: ${({ $first }) => ($first ? "0 0 8px" : "16px 0 8px")};
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #546e7a;

    .MuiSvgIcon-root {
      font-size: 18px;
      color: ${({ theme }) => theme.color.primary.dark};
    }
  }
`;

const ActionsList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ActionItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  padding: ${({ $muted }) => ($muted ? "6px 8px" : "8px")};
  border-radius: 8px;
  font-size: ${({ $muted }) => ($muted ? "13px" : "14px")};
  line-height: 1.45;
  color: ${({ $muted }) => ($muted ? "#607d8b" : "#37474f")};
  opacity: ${({ $muted }) => ($muted ? 0.82 : 1)};
  background: ${({ $muted }) => ($muted ? "#fafbfc" : "transparent")};
  border: ${({ $muted }) => ($muted ? "1px solid #eef2f4" : "none")};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme, $muted }) =>
      $muted ? "#f5f7f8" : theme.color.primary.light};
  }

  .MuiSvgIcon-root.action-icon {
    flex: 0 0 auto;
    margin-top: 2px;
    font-size: ${({ $muted }) => ($muted ? "16px" : "18px")};
    color: ${({ $done, $muted, theme }) => {
      if ($done) return "#2e7d32";
      if ($muted) return "#90a4ae";
      return theme.color.primary.dark;
    }};
  }
`;

const ActionMeta = styled.span`
  color: #78909c;
`;

const ActionNote = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #78909c;
`;

const SortableShell = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-bottom: 8px;
  border: 1px solid
    ${({ $over, theme }) => ($over ? theme.color.primary.main : "#e8eef0")};
  border-radius: 10px;
  background: ${({ $dragging, theme }) =>
    $dragging ? theme.color.primary.light : "#fff"};
  opacity: ${({ $dragging }) => ($dragging ? 0.72 : 1)};
  transition: border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease;
  overflow: hidden;

  &:hover {
    border-color: ${({ theme }) => theme.color.primary.main};
    background: ${({ theme }) => theme.color.primary.light};
  }
`;

const DragHandle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  border: 0;
  padding: 0;
  background: transparent;
  color: #90a4ae;
  cursor: grab;

  &:hover {
    color: ${({ theme }) => theme.color.primary.dark};
    background: ${({ theme }) => theme.color.primary.light};
  }

  &:active {
    cursor: grabbing;
  }
`;

const CompactRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  border: 0;
  border-radius: 0;
  padding: 10px 12px 10px 4px;
  background: transparent;
  cursor: pointer;
`;

const CompactBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactTitle = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: #37474f;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CompactMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #78909c;
`;

const EditPanel = styled.div`
  padding: 4px 4px 14px 36px;
  margin: 0 0 6px;

  .MuiFormControl-root,
  .MuiTextField-root {
    background: transparent;
  }

  .MuiInput-underline:before {
    border-bottom-color: rgba(0, 0, 0, 0.18);
  }

  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border-bottom-color: ${({ theme }) => theme.color.primary.main};
  }

  .MuiInput-underline:after {
    border-bottom-color: ${({ theme }) => theme.color.primary.dark};
  }
`;

const MetaHint = styled.p`
  margin: 0 0 10px;
  font-size: 13px;
  color: #607681;
  line-height: 1.4;
`;

/** Campo “Responsável” no mesmo espírito do seletor de fala. */
const AssigneeField = styled.div`
  flex: 1 1 180px;
  min-width: 160px;
  max-width: 280px;
`;

const AssigneeLabel = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.2;
  color: #78909c;
  margin-bottom: 2px;
`;

const AssigneeTrigger = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  width: 100%;
  margin: 0;
  padding: 4px 4px 6px 0;
  border: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: border-color 0.15s ease;

  &:hover {
    border-bottom-color: ${({ theme }) => theme.color.primary.main};
  }

  &:focus {
    outline: none;
    border-bottom-color: ${({ theme }) => theme.color.primary.dark};
    border-bottom-width: 2px;
    padding-bottom: 5px;
  }

  .assignee-name-row {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    max-width: 100%;
  }

  strong {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: ${({ theme, $empty }) =>
      $empty ? "#90a4ae" : theme.color.primary.dark};
    border-bottom: ${({ $empty, theme }) =>
      $empty ? "none" : `1px dotted ${theme.color.primary.main}88`};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .assignee-chevron {
    flex: 0 0 auto;
    font-size: 20px;
    color: ${({ theme }) => theme.color.primary.main};
    opacity: 0.85;
  }
`;

const AssigneeMeta = styled.span`
  font-size: 12px;
  line-height: 1.35;
  color: #78909c;
`;

const PersonOptionLabel = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
`;

const PersonOptionName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #263238;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const PersonOptionMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const SelectHintItem = styled.span`
  display: block;
  font-family: inherit;
  font-style: normal;
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
  line-height: 1.3;
`;

const SelectAddItem = styled.span`
  display: block;
  font-family: inherit;
  font-style: normal;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary.dark};
  line-height: 1.3;
`;

function personMetaLine(opt) {
  if (!opt) return "";
  return formatSpeakerMetaLine({
    role: opt.role,
    responsibility: opt.responsibility
  });
}

function resolveAssigneeOption(action, options) {
  if (!action || !options || !options.length) return null;
  if (action.assigneePersonId) {
    const byId = options.find(o => o.id === action.assigneePersonId);
    if (byId) return byId;
  }
  const key = normalizeNameKey(action.assignee);
  if (!key) return null;
  return options.find(o => normalizeNameKey(o.name) === key) || null;
}

function statusChipProps(status, t) {
  if (status === ACTION_STATUS.DONE) {
    return { label: getActionStatusLabel(status), color: "primary" };
  }
  if (status === ACTION_STATUS.IN_PROGRESS) {
    return {
      label: t("smartMinutes.action.partial"),
      style: { background: "#fff3e0", color: "#e65100" }
    };
  }
  return {
    label: getActionStatusLabel(status) || status,
    style: { background: "#eceff1", color: "#546e7a" }
  };
}

function ActionItems({ actions, showStatus, showOrigin, muted, t }) {
  if (!actions.length) return null;
  return (
    <ActionsList>
      {actions.map((action, idx) => {
        const status = getActionStatus(action);
        const done = status === ACTION_STATUS.DONE;
        const chip = statusChipProps(status, t);
        let Icon = AssignmentIcon;
        if (done) Icon = CheckCircleIcon;
        else if (status === ACTION_STATUS.IN_PROGRESS) Icon = TimelapseIcon;

        return (
          <ActionItem
            key={
              action.ataId && action.id
                ? `${action.ataId}-${action.id}`
                : action.id || `act-${idx}`
            }
            $done={done}
            $muted={muted}
          >
            <Icon className="action-icon" />
            <span>
              {action.description}
              {(action.assignee || action.dueHint) && (
                <ActionMeta>
                  {" — "}
                  {[action.assignee, action.dueHint]
                    .filter(Boolean)
                    .join(" · ")}
                </ActionMeta>
              )}
              {showStatus && (
                <div style={{ marginTop: 4 }}>
                  <Chip size="small" {...chip} />
                </div>
              )}
              {action.statusNote ? (
                <ActionNote>{action.statusNote}</ActionNote>
              ) : null}
              {showOrigin && (action.studentName || action.ataTitle) && (
                <ActionNote>
                  {muted && action.ataTitle && !done
                    ? t("smartMinutes.action.stillOpenFrom", {
                        title: action.ataTitle
                      })
                    : [
                        action.studentName,
                        action.ataTitle
                          ? t("smartMinutes.action.ataRef", {
                              title: action.ataTitle
                            })
                          : null
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                </ActionNote>
              )}
            </span>
          </ActionItem>
        );
      })}
    </ActionsList>
  );
}

ActionItems.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showStatus: PropTypes.bool,
  showOrigin: PropTypes.bool,
  muted: PropTypes.bool,
  t: PropTypes.func.isRequired
};

ActionItems.defaultProps = {
  showStatus: false,
  showOrigin: true,
  muted: false
};

function EditableActionItems({ actions, onChange, assigneeOptions, t }) {
  const [expandedId, setExpandedId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [assigneeMenu, setAssigneeMenu] = useState({
    anchor: null,
    key: null
  });
  const [typingAssigneeKey, setTypingAssigneeKey] = useState(null);
  const options = assigneeOptions || [];

  const updateAt = (index, patch) => {
    const next = (actions || []).map((a, i) =>
      i === index ? { ...a, ...patch } : a
    );
    onChange(next);
  };

  const removeAt = index => {
    onChange((actions || []).filter((_, i) => i !== index));
  };

  const moveItem = (fromIndex, toIndex) => {
    if (
      !Number.isFinite(fromIndex) ||
      !Number.isFinite(toIndex) ||
      fromIndex === toIndex
    ) {
      return;
    }
    const list = [...(actions || [])];
    if (
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= list.length ||
      toIndex >= list.length
    ) {
      return;
    }
    const [item] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, item);
    onChange(list);
  };

  const closeAssigneeMenu = () => {
    setAssigneeMenu({ anchor: null, key: null });
  };

  const pickAssignee = (index, opt) => {
    const id = opt && opt.id ? String(opt.id) : "";
    const isGuardian = id.startsWith("guardian:");
    const isStaff = id.startsWith("staff:");
    const patch = {
      assignee: opt.name,
      assigneePersonId: opt.id,
      assigneeSource: "manual_pick"
    };
    if (isGuardian) patch.audience = "family";
    else if (isStaff) patch.audience = "school";
    updateAt(index, patch);
    setTypingAssigneeKey(null);
    closeAssigneeMenu();
  };

  const clearAssignee = index => {
    updateAt(index, {
      assignee: "",
      assigneePersonId: null,
      assigneeSource: null
    });
    setTypingAssigneeKey(null);
    closeAssigneeMenu();
  };

  const menuActionIndex =
    assigneeMenu.key != null
      ? (actions || []).findIndex(
          (a, i) => (a.id || `edit-act-${i}`) === assigneeMenu.key
        )
      : -1;
  const menuAction =
    menuActionIndex >= 0 ? (actions || [])[menuActionIndex] : null;
  const menuCurrent =
    menuAction != null ? resolveAssigneeOption(menuAction, options) : null;
  let menuCurrentId = "";
  if (menuCurrent) {
    menuCurrentId = menuCurrent.id;
  } else if (menuAction && menuAction.assigneePersonId) {
    menuCurrentId = menuAction.assigneePersonId;
  }

  return (
    <div>
      {(actions || []).map((action, idx) => {
        const key = action.id || `edit-act-${idx}`;
        const open = expandedId === key;
        const status = getActionStatus(action);
        let Icon = AssignmentIcon;
        if (status === ACTION_STATUS.DONE) Icon = CheckCircleIcon;
        else if (status === ACTION_STATUS.IN_PROGRESS) Icon = TimelapseIcon;
        const selectedOpt = resolveAssigneeOption(action, options);
        const selectedMeta = personMetaLine(selectedOpt);
        const isTyping = typingAssigneeKey === key;
        const hasAssignee = !!(action.assignee || selectedOpt);

        return (
          <div key={key}>
            <SortableShell
              $dragging={dragIndex === idx}
              $over={
                overIndex === idx && dragIndex !== null && dragIndex !== idx
              }
              onDragOver={e => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overIndex !== idx) setOverIndex(idx);
              }}
              onDragLeave={() => {
                if (overIndex === idx) setOverIndex(null);
              }}
              onDrop={e => {
                e.preventDefault();
                const from = Number(
                  e.dataTransfer.getData("text/plain") || dragIndex
                );
                moveItem(from, idx);
                setDragIndex(null);
                setOverIndex(null);
              }}
            >
              <DragHandle
                type="button"
                title={t("smartMinutes.action.dragReorder")}
                draggable
                onClick={e => e.stopPropagation()}
                onDragStart={e => {
                  setDragIndex(idx);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", String(idx));
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
              >
                <DragIndicatorIcon style={{ fontSize: 18 }} />
              </DragHandle>
              <CompactRow
                type="button"
                onClick={() => setExpandedId(open ? null : key)}
              >
                <Icon
                  style={{
                    fontSize: 18,
                    marginTop: 2,
                    color:
                      status === ACTION_STATUS.DONE
                        ? "#2e7d32"
                        : appTheme.color.primary.dark
                  }}
                />
                <CompactBody>
                  <CompactTitle>
                    {action.description ||
                      t("smartMinutes.action.noDescription")}
                  </CompactTitle>
                  <CompactMeta>
                    {[
                      action.assignee,
                      action.dueHint,
                      action.audience === "family"
                        ? t("smartMinutes.action.family")
                        : t("smartMinutes.action.schoolAudience"),
                      getActionStatusLabel(status)
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </CompactMeta>
                </CompactBody>
                {open ? (
                  <ExpandLessIcon style={{ color: "#78909c" }} />
                ) : (
                  <ExpandMoreIcon style={{ color: "#78909c" }} />
                )}
              </CompactRow>
            </SortableShell>
            <Collapse in={open}>
              <EditPanel>
                <TextField
                  fullWidth
                  multiline
                  size="small"
                  variant="standard"
                  label={t("smartMinutes.action.description")}
                  value={action.description || ""}
                  onChange={e => updateAt(idx, { description: e.target.value })}
                  style={{ marginBottom: 4 }}
                />
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-end"
                  style={{ gap: 16 }}
                >
                  <AssigneeField data-assignee-field>
                    <AssigneeLabel>
                      {t("smartMinutes.action.assignee")}
                    </AssigneeLabel>
                    {isTyping ? (
                      <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        variant="standard"
                        placeholder={t(
                          "smartMinutes.action.assigneeTypePlaceholder"
                        )}
                        value={action.assignee || ""}
                        onChange={e => {
                          const name = e.target.value;
                          const hit = options.find(
                            o =>
                              normalizeNameKey(o.name) ===
                              normalizeNameKey(name)
                          );
                          updateAt(idx, {
                            assignee: name,
                            assigneePersonId: hit ? hit.id : null,
                            assigneeSource: hit ? "manual_pick" : "manual_text"
                          });
                        }}
                        onBlur={() => setTypingAssigneeKey(null)}
                        InputProps={{
                          endAdornment: (
                            <ArrowDropDownIcon
                              style={{
                                fontSize: 20,
                                color: appTheme.color.primary.main,
                                cursor: "pointer"
                              }}
                              onMouseDown={e => {
                                e.preventDefault();
                                const field = e.currentTarget.closest(
                                  "[data-assignee-field]"
                                );
                                setTypingAssigneeKey(null);
                                setAssigneeMenu({
                                  anchor: field || e.currentTarget,
                                  key
                                });
                              }}
                            />
                          )
                        }}
                      />
                    ) : (
                      <AssigneeTrigger
                        type="button"
                        $empty={!hasAssignee}
                        title={t("smartMinutes.action.chooseAssignee")}
                        onClick={e =>
                          setAssigneeMenu({
                            anchor: e.currentTarget,
                            key
                          })}
                      >
                        <span className="assignee-name-row">
                          <strong>
                            {hasAssignee
                              ? action.assignee ||
                                (selectedOpt && selectedOpt.name)
                              : t("smartMinutes.action.assigneePlaceholder")}
                          </strong>
                          <ArrowDropDownIcon className="assignee-chevron" />
                        </span>
                        {selectedMeta ? (
                          <AssigneeMeta>{selectedMeta}</AssigneeMeta>
                        ) : null}
                      </AssigneeTrigger>
                    )}
                  </AssigneeField>
                  <TextField
                    size="small"
                    variant="standard"
                    label={t("smartMinutes.action.due")}
                    value={action.dueHint || ""}
                    onChange={e => updateAt(idx, { dueHint: e.target.value })}
                    style={{ flex: "1 1 120px" }}
                  />
                  <FormControl
                    size="small"
                    variant="standard"
                    style={{ minWidth: 120 }}
                  >
                    <InputLabel id={`ata-audience-${key}`}>
                      {t("smartMinutes.action.audience")}
                    </InputLabel>
                    <Select
                      labelId={`ata-audience-${key}`}
                      value={action.audience === "family" ? "family" : "school"}
                      onChange={e =>
                        updateAt(idx, { audience: e.target.value })}
                    >
                      <MenuItem value="school">
                        {t("smartMinutes.action.schoolAudience")}
                      </MenuItem>
                      <MenuItem value="family">
                        {t("smartMinutes.action.family")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    variant="standard"
                    style={{ minWidth: 140 }}
                  >
                    <InputLabel id={`ata-status-${key}`}>
                      {t("smartMinutes.action.status")}
                    </InputLabel>
                    <Select
                      labelId={`ata-status-${key}`}
                      value={getActionStatus(action)}
                      onChange={e => updateAt(idx, { status: e.target.value })}
                    >
                      <MenuItem value={ACTION_STATUS.PENDING}>
                        {t("smartMinutes.action.pending")}
                      </MenuItem>
                      <MenuItem value={ACTION_STATUS.IN_PROGRESS}>
                        {t("smartMinutes.action.inProgress")}
                      </MenuItem>
                      <MenuItem value={ACTION_STATUS.DONE}>
                        {t("smartMinutes.action.done")}
                      </MenuItem>
                      <MenuItem value={ACTION_STATUS.CANCELLED}>
                        {t("smartMinutes.action.cancelled")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    color="secondary"
                    variant="text"
                    onClick={() => removeAt(idx)}
                    style={{ minWidth: 0, padding: "6px 8px" }}
                    title={t("smartMinutes.action.remove")}
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </Box>
              </EditPanel>
            </Collapse>
          </div>
        );
      })}
      <Menu
        anchorEl={assigneeMenu.anchor}
        open={Boolean(assigneeMenu.anchor) && menuActionIndex >= 0}
        onClose={closeAssigneeMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ style: { maxHeight: 360, minWidth: 240 } }}
      >
        <MenuItem disabled dense>
          <SelectHintItem>
            {t("smartMinutes.action.chooseAssignee")}
          </SelectHintItem>
        </MenuItem>
        {options.map(opt => {
          const meta = personMetaLine(opt);
          const selected = opt.id === menuCurrentId;
          return (
            <MenuItem
              key={opt.id}
              selected={selected}
              onClick={() => pickAssignee(menuActionIndex, opt)}
            >
              <PersonOptionLabel>
                <PersonOptionName>{opt.name}</PersonOptionName>
                {meta ? <PersonOptionMeta>{meta}</PersonOptionMeta> : null}
              </PersonOptionLabel>
            </MenuItem>
          );
        })}
        {menuAction && (menuAction.assignee || menuCurrent) ? (
          <MenuItem onClick={() => clearAssignee(menuActionIndex)}>
            <SelectHintItem>
              {t("smartMinutes.action.clearAssignee")}
            </SelectHintItem>
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (assigneeMenu.key) setTypingAssigneeKey(assigneeMenu.key);
            closeAssigneeMenu();
          }}
        >
          <SelectAddItem>
            {t("smartMinutes.action.typeAssigneeEllipsis")}
          </SelectAddItem>
        </MenuItem>
      </Menu>
    </div>
  );
}

EditableActionItems.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onChange: PropTypes.func.isRequired,
  assigneeOptions: PropTypes.arrayOf(PropTypes.shape({})),
  t: PropTypes.func.isRequired
};

EditableActionItems.defaultProps = {
  assigneeOptions: []
};

/**
 * Encaminhamentos em dois grupos: Família e nome do colégio.
 * editable=true: lista compacta com expand para editar.
 */
function AtaActionsGrouped({
  ata,
  actions,
  showStatus,
  showOrigin,
  muted,
  editable,
  onChange
}) {
  const { t } = useTranslation();
  const list = actions || (ata && ata.actions) || [];
  const schoolLabel =
    UserStore.getSchoolName() || t("smartMinutes.action.schoolAudience");

  if (editable && onChange) {
    const assigneeOptions = listPersonOptions(ata).filter(
      o => o && o.name && !String(o.id || "").startsWith("student:")
    );
    const addAction = () => {
      onChange([
        ...list,
        {
          id: `act_manual_${Date.now()}`,
          description: "",
          assignee: "",
          dueHint: t("smartMinutes.action.dueNextMeeting"),
          audience: "school",
          status: ACTION_STATUS.PENDING,
          reviewAtNextMeeting: true,
          priority: "medium"
        }
      ]);
    };

    return (
      <>
        <MetaHint>{t("smartMinutes.action.editHint")}</MetaHint>
        <EditableActionItems
          actions={list}
          onChange={onChange}
          assigneeOptions={assigneeOptions}
          t={t}
        />
        <Box mt={1}>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addAction}
          >
            {t("smartMinutes.action.new")}
          </Button>
        </Box>
      </>
    );
  }

  if (!list.length) return null;

  const { family, school } = groupActionsByAudience(list, ata);

  return (
    <>
      {family.length > 0 && (
        <>
          <GroupTitle $first variant="subtitle2">
            <PeopleIcon />
            {t("smartMinutes.action.family")}
          </GroupTitle>
          <ActionItems
            actions={family}
            showStatus={showStatus}
            showOrigin={showOrigin}
            muted={muted}
            t={t}
          />
        </>
      )}
      {school.length > 0 && (
        <>
          <GroupTitle $first={!family.length} variant="subtitle2">
            <BusinessIcon />
            {schoolLabel}
          </GroupTitle>
          <ActionItems
            actions={school}
            showStatus={showStatus}
            showOrigin={showOrigin}
            muted={muted}
            t={t}
          />
        </>
      )}
    </>
  );
}

AtaActionsGrouped.propTypes = {
  ata: PropTypes.shape({
    actions: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  actions: PropTypes.arrayOf(PropTypes.shape({})),
  showStatus: PropTypes.bool,
  showOrigin: PropTypes.bool,
  muted: PropTypes.bool,
  editable: PropTypes.bool,
  onChange: PropTypes.func
};

AtaActionsGrouped.defaultProps = {
  ata: null,
  actions: null,
  showStatus: false,
  showOrigin: true,
  muted: false,
  editable: false,
  onChange: null
};

export default AtaActionsGrouped;
